"use client";

import { useState, useEffect, useMemo } from "react";
import { Plus, Minus, Search, Utensils, Moon, Sun, Dumbbell, Wine, Cigarette, Activity, Smartphone, Check, Zap, Flower2, Mic, Copy, Loader2, ChevronDown, Rocket, Pill, Droplets, Flame } from "lucide-react";
import SimpleModal from "@/components/SimpleModal";
import VoiceParser from "./VoiceParser";
import clsx from "clsx";
import { DailyLog, FoodItem } from "@/types";
import { getTodayISO, getPreviousDayISO, getLast7DaysISO } from "@/utils/date";
import { fetchLogs } from "@/utils/storage";
import DatePicker from "@/components/DatePicker";
import { motion, AnimatePresence } from "framer-motion";
import LongevityCard from "./LongevityCard";
import { UserProfile } from "@/types";
import { fetchUserProfile as fetchUserProfileStorage } from "@/utils/storage";
import { defaultProfile, calculateDailyScore, calculateLongevityAge, calculateDailyProtein, calculateCaloriesBurned, calculateCoreScore } from "@/utils/scoring";

interface DailyInputProps {
    onSave: (log: DailyLog) => void;
    currentLog: DailyLog | null;
}

const WORKOUT_OPTIONS = {
    "Strength": [
        "Chest workout", "Back workout", "Shoulders", "Arms (biceps + triceps)",
        "Legs – Strength Day", "Glutes workout", "Full-body strength",
        "Abs/Core workout", "Calisthenics", "Machine strength"
    ],
    "Cardio": [
        "Running – Light Jog", "Running – Moderate", "Running – Intense",
        "Walking – Normal", "Walking – Fast", "Cycling – Outdoor", "Cycling – Indoor",
        "HIIT workout", "Rowing machine", "Stairmaster", "Elliptical", "Swimming", "Jump rope"
    ],
    "Mobility": [
        "Rest",
        "Yoga – Hatha", "Yoga – Vinyasa", "Stretching", "Pilates",
        "Foam rolling", "Breathwork", "Meditation"
    ],
    "Sports": [
        "Badminton – Casual", "Badminton – Intense", "Tennis – Casual",
        "Tennis – Intense", "Football", "Basketball", "Cricket – Casual",
        "Cricket – Match", "Hiking"
    ]
};

const COMMON_SUPPLEMENTS = [
    // Vitamins & Minerals
    "Vitamin D3", "Vitamin K2", "Vitamin C", "Vitamin B12", "B-Complex", "Methylfolate",
    "Vitamin A", "Vitamin E", "Magnesium Glycinate", "Magnesium Threonate", "Magnesium Citrate",
    "Zinc", "Iron", "Calcium", "Iodine", "Selenium", "Chromium", "Boron", "Potassium", "Copper",

    // Longevity & Cellular Health
    "NMN", "NR", "Resveratrol", "Pterostilbene", "Spermidine", "Fisetin", "Quercetin",
    "CoQ10", "PQQ", "Urolithin A", "Ca-AKG", "Apigenin", "Glutathione", "NAC",
    "Alpha Lipoic Acid", "Astaxanthin", "Sulforaphane",

    // Performance & Recovery
    "Creatine Monohydrate", "Whey Protein", "Casein", "Plant Protein", "BCAAs", "EAAs",
    "L-Glutamine", "L-Carnitine", "Beta-Alanine", "Citrulline Malate", "L-Arginine",
    "Taurine", "HMB", "Tart Cherry Extract", "Electrolytes",

    // Cognitive Notropics & Adaptogens
    "Omega-3 (Fish Oil)", "Krill Oil", "Algae Oil", "Lion's Mane", "Cordyceps", "Reishi",
    "Chaga", "Turkey Tail", "Ashwagandha", "Rhodiola Rosea", "L-Theanine", "Ginkgo Biloba",
    "Bacopa Monnieri", "Panax Ginseng", "Phosphatidylserine", "Huperzine A", "Alpha-GPC",
    "L-Tyrosine", "Mucuna Pruriens",

    // Sleep & Mood
    "Melatonin", "Glycine", "GABA", "Valerian Root", "Chamomile", "Lemon Balm",
    "Passion Flower", "5-HTP", "Tryptophan", "Saffron", "Inositol",

    // Gut & Digestion
    "Probiotics", "Prebiotics", "Digestive Enzymes", "Betaine HCl", "Butyrate",
    "Colostrum", "Psyllium Husk", "Fiber Blend",

    // Hormonal Support
    "Tongkat Ali", "Fadogia Agrestis", "Maca Root", "Tribulus Terrestris", "DIM",
    "Saw Palmetto", "Fenugreek", "Shilajit",

    // Joint & Skin
    "Collagen Peptides", "Hyaluronic Acid", "Glucosamine", "Chondroitin", "MSM",
    "Curcumin/Turmeric", "Boswellia", "Biotin", "Silica"
];

const hasMeaningfulData = (log: DailyLog | null): boolean => {
    if (!log) return false;
    return (
        (log.meals?.some((m) => m.items.length > 0)) ||
        (log.workouts?.some(w => w.type !== "Rest" || (w.duration || 0) > 0) || (log.steps || 0) > 0) ||
        ((typeof log.sleep === 'object' ? log.sleep.hours : log.sleep) > 0) ||
        (log.water > 0) ||
        ((log.supplementProtein || 0) > 0) ||
        (log.alcohol?.type && log.alcohol.type !== "" && log.alcohol.type !== "None") ||
        (log.smoking?.type === "Yes") ||
        (log.meditation && log.meditation > 0) ||
        (log.sunlight && log.sunlight > 0) ||
        (log.screenTime && log.screenTime > 0) ||
        (log.screenFreeBeforeBed === true) ||
        (log.socialConnect && log.socialConnect > 0) ||
        (log.stress !== undefined && log.stress > 0)
    );
};

export default function DailyInput({ onSave, currentLog }: DailyInputProps) {

    const [log, setLog] = useState<DailyLog>(currentLog || {
        date: getTodayISO(),
        meals: [
            { name: "Breakfast", items: [] },
            { name: "Snacks", items: [] },
            { name: "Lunch", items: [] },
            { name: "Dinner", items: [] },
        ],
        workouts: [{ type: "Rest", duration: 0, calories: 0 }],
        steps: 0,
        sleep: 0,
        water: 0,
        supplements: {
            "Omega-3": false,
            "Vit D + K2": false,
            "Multivitamin": false,
            "Ashwagandha": false,
            "Creatine": false,
            "Magnesium": false,
            "Rhodiola": false,
            "Electrolytes / Vit C": false,
        },
        mood: "",
        alcohol: { type: "", units: 0 },
        smoking: { type: "No", quantity: 0 },
        stress: 0,
        meditation: 0,
        sunlight: 0,
        screenTime: 0,
        screenFreeBeforeBed: false,
    });

    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [pastLogs, setPastLogs] = useState<DailyLog[]>([]);

    useEffect(() => {
        const loadProfile = async () => {
            const p = await fetchUserProfileStorage();
            setProfile(p);
        };
        loadProfile();
    }, []);

    const { longevityAge, longevityScore } = useMemo(() => {
        if (!profile) return {
            longevityAge: null,
            longevityScore: null
        };

        // Determine visibility based on SAVED data (currentLog)
        // We check if currentLog exists, matches the current date, and has meaningful data
        // This ensures the card only appears after the user has SAVED the log
        const isSaved = currentLog && currentLog.date === log.date && hasMeaningfulData(currentLog);

        if (!isSaved) {
            return {
                longevityAge: null,
                longevityScore: null
            };
        }

        // Calculate based on LOCAL log (live input) so values update as they type
        // logic: Once visible (saved once), we show live updates
        const age = calculateLongevityAge(log, profile);
        const score = calculateDailyScore(log);

        return {
            longevityAge: age,
            longevityScore: score
        };
    }, [log, profile, currentLog]);

    useEffect(() => {
        if (currentLog) {
            setLog((prev) => {
                // If the date is different, it's a completely new log context -> overwrite full state
                if (prev.date !== currentLog.date) {
                    return currentLog;
                }
                // If dates match, it's likely an async update (like AI analysis arriving).
                // Merge only the new AI data to avoid wiping user's typed input.
                return {
                    ...prev,
                    aiAnalysis: currentLog.aiAnalysis ?? prev.aiAnalysis,
                    workouts: currentLog.workouts ?? prev.workouts,
                    steps: currentLog.steps ?? prev.steps
                };
            });
        }
    }, [currentLog]);


    // Sync state when date changes (load existing log or reset)
    useEffect(() => {
        const loadLog = async () => {
            if (log.date) {
                const storedLogs = await fetchLogs();
                const existingLog = storedLogs.find((l) => l.date === log.date);
                setPastLogs(storedLogs);

                if (existingLog) {
                    // Ensure meals are in the correct order: Breakfast, Snacks, Lunch, Dinner
                    const MEAL_ORDER = ["Breakfast", "Snacks", "Lunch", "Dinner"];
                    const sortedMeals = [...existingLog.meals].sort((a, b) => {
                        return MEAL_ORDER.indexOf(a.name) - MEAL_ORDER.indexOf(b.name);
                    });

                    // If "Snacks" is missing (old logs might not have it), add it
                    if (!sortedMeals.find(m => m.name === "Snacks")) {
                        sortedMeals.splice(1, 0, { name: "Snacks", items: [] });
                    }

                    // Migration for old logs: workout -> workouts
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const rawLog = existingLog as any;
                    const workouts = existingLog.workouts || (rawLog.workout ? [{
                        type: rawLog.workout.type,
                        duration: rawLog.workout.duration,
                        calories: rawLog.workout.calories
                    }] : [{ type: "Rest", duration: 0, calories: 0 }]);

                    const steps = existingLog.steps ?? (rawLog.workout?.steps || 0);

                    setLog({ ...existingLog, meals: sortedMeals, workouts, steps });
                } else {
                    setLog({
                        date: log.date,
                        meals: [
                            { name: "Breakfast", items: [] },
                            { name: "Snacks", items: [] },
                            { name: "Lunch", items: [] },
                            { name: "Dinner", items: [] },
                        ],
                        workouts: [{ type: "Rest", duration: 0, calories: 0 }],
                        steps: 0,
                        sleep: 0,
                        water: 0,
                        supplements: {
                            "Omega-3": false,
                            "Vit D + K2": false,
                            "Multivitamin": false,
                            "Ashwagandha": false,
                            "Creatine": false,
                            "Magnesium": false,
                            "Rhodiola": false,
                            "Electrolytes / Vit C": false,
                        },
                        mood: "",
                        alcohol: { type: "", units: 0 },
                        smoking: { type: "No", quantity: 0 },
                        stress: 0,
                        meditation: 0,
                        sunlight: 0,
                        screenTime: 0,
                        screenFreeBeforeBed: false,
                        socialConnect: 0,
                    });
                }
            }
        };
        loadLog();
    }, [log.date]);

    // Initialize date if empty (on mount)
    useEffect(() => {
        if (!currentLog && !log.date) {
            setLog((prev) => ({
                ...prev,
                date: getTodayISO(),
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLog]);

    const [foodQuery, setFoodQuery] = useState("");
    const [foodResults, setFoodResults] = useState<FoodItem[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showNutritionVoice, setShowNutritionVoice] = useState(false);
    const [showLifestyleVoice, setShowLifestyleVoice] = useState(false);
    const [showWorkoutVoice, setShowWorkoutVoice] = useState(false);
    const [showSleepVoice, setShowSleepVoice] = useState(false);
    const [activeMealIndex, setActiveMealIndex] = useState<number | null>(null);
    const [isWorkoutDropdownOpen, setIsWorkoutDropdownOpen] = useState<number | null>(null);
    const [workoutSearchQuery, setWorkoutSearchQuery] = useState("");
    const [showComingSoon, setShowComingSoon] = useState(false);


    // Copy Yesterday State
    const [isCopying, setIsCopying] = useState(false);
    const [copyStatus, setCopyStatus] = useState<string | null>(null);
    const [newSupplement, setNewSupplement] = useState("");
    const [showSuppSuggestions, setShowSuppSuggestions] = useState(false);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);


    useEffect(() => {
        if (foodQuery.length > 1) {
            setIsSearching(true);
            import("@/data/food_data.json").then((data) => {
                const results = (data.default as FoodItem[]).filter((item) =>
                    item.name.toLowerCase().includes(foodQuery.toLowerCase())
                );
                setFoodResults(results.slice(0, 5));
                setIsSearching(false);
            });
        } else {
            setFoodResults([]);
        }
    }, [foodQuery]);

    const addFoodToMeal = (food: FoodItem) => {
        if (activeMealIndex === null) return;

        const newMeals = [...log.meals];
        newMeals[activeMealIndex].items.push({
            foodId: food.id,
            name: food.name,
            quantity: 1,
            calories: food.calories,
            protein: food.protein,
        });
        setLog({ ...log, meals: newMeals });
        setFoodQuery("");
        setFoodResults([]);
        setActiveMealIndex(null);
    };

    const updateFoodQuantity = (mealIndex: number, itemIndex: number, change: number) => {
        const newMeals = [...log.meals];
        const item = newMeals[mealIndex].items[itemIndex];
        const newQuantity = item.quantity + change;

        if (newQuantity <= 0) {
            newMeals[mealIndex].items.splice(itemIndex, 1);
        } else {
            item.quantity = newQuantity;
        }
        setLog({ ...log, meals: newMeals });
    };

    const toggleSupplement = (name: string) => {
        setLog({
            ...log,
            supplements: {
                ...log.supplements,
                [name]: !log.supplements[name]
            },
        });
    };

    const addCustomSupplement = () => {
        if (!newSupplement.trim()) return;
        setLog({
            ...log,
            supplements: {
                ...log.supplements,
                [newSupplement.trim()]: true
            }
        });
        setNewSupplement("");
    };

    const copyPreviousDayData = async () => {
        if (!log.date || isCopying) return;
        setIsCopying(true);
        setCopyStatus(null);

        try {
            const previousDate = getPreviousDayISO(log.date);
            // Artificial delay for better UX (so user sees the loading state)
            await new Promise(resolve => setTimeout(resolve, 600));

            const storedLogs = await fetchLogs();
            const previousLog = storedLogs.find((l) => l.date === previousDate);

            if (previousLog) {
                setLog({
                    ...previousLog,
                    date: log.date, // KEEP current date
                });
                setCopyStatus("Copied!");
            } else {
                setCopyStatus("No data found");
            }
        } catch {
            setCopyStatus("Error");
        } finally {
            setIsCopying(false);
            // Clear status after 2 seconds
            setTimeout(() => setCopyStatus(null), 2500);
        }
    };

    const getSleepHours = (sleep: number | { hours: number; quality?: number }) => {
        return typeof sleep === 'object' ? sleep.hours : sleep;
    };

    return (
        <div className="flex flex-col gap-4 pb-20">
            {/* Header Section */}
            <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center relative">
                    <h1 className="text-sm font-medium tracking-[0.2em] text-white uppercase">Start Your Day</h1>

                    <div className="flex items-center gap-2">
                        <AnimatePresence>
                            {copyStatus && (
                                <motion.span
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0 }}
                                    className={clsx(
                                        "text-[10px] font-medium",
                                        copyStatus === "Copied!" ? "text-green-400" : "text-amber-400"
                                    )}
                                >
                                    {copyStatus}
                                </motion.span>
                            )}
                        </AnimatePresence>

                        <button
                            onClick={copyPreviousDayData}
                            disabled={isCopying}
                            className={clsx(
                                "flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-all group",
                                "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20",
                                isCopying ? "cursor-wait opacity-70" : "cursor-pointer"
                            )}
                        >
                            {isCopying ? (
                                <Loader2 size={14} className="animate-spin text-green-500" />
                            ) : (
                                <Copy size={14} className="text-green-500" />
                            )}
                            <span className="text-xs font-bold text-text-muted uppercase tracking-wider group-hover:text-white transition-colors">
                                {isCopying ? "Copying..." : "Copy Yesterday"}
                            </span>
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex-1">
                        <DatePicker
                            value={log.date}
                            onChange={(isoDate) => setLog({ ...log, date: isoDate })}
                        />
                    </div>



                </div>


            </div>

            {/* Longevity Age Card - Only show if we have calculated values for this specific log */}
            {longevityAge !== null && longevityScore !== null && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mb-2"
                >
                    <LongevityCard
                        chronologicalAge={profile?.age || 30}
                        longevityAge={longevityAge}
                        longevityScore={longevityScore}
                        weight={profile?.weight || 70}
                        height={profile?.height || 175}
                    />
                </motion.div>
            )}

            {/* Daily Stats Grid - Shows only when Longevity Card shows (meaning data exists) */}
            {longevityAge !== null && longevityScore !== null && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="grid grid-cols-3 gap-2 mb-4"
                >
                    {/* Protein */}
                    <div className="glass-card p-3 flex flex-col items-center justify-center text-center">
                        <Zap size={16} className="text-emerald-400 mb-1" />
                        <div className="text-[10px] uppercase text-text-muted font-bold tracking-wider mb-0.5">Protein</div>
                        <div className="text-lg font-bold text-white">{Math.round(calculateDailyProtein(log))}g</div>
                    </div>

                    {/* Sleep */}
                    <div className="glass-card p-3 flex flex-col items-center justify-center text-center">
                        <Moon size={16} className="text-indigo-400 mb-1" />
                        <div className="text-[10px] uppercase text-text-muted font-bold tracking-wider mb-0.5">Sleep</div>
                        <div className="text-lg font-bold text-white">{getSleepHours(log.sleep)}h</div>
                    </div>

                    {/* Burned */}
                    <div className="glass-card p-3 flex flex-col items-center justify-center text-center">
                        <Flame size={16} className="text-orange-400 mb-1" />
                        <div className="text-[10px] uppercase text-text-muted font-bold tracking-wider mb-0.5">Burned</div>
                        <div className="text-lg font-bold text-white">{calculateCaloriesBurned(log, profile || undefined)}</div>
                    </div>

                    {/* Steps */}
                    <div className="glass-card p-3 flex flex-col items-center justify-center text-center">
                        <Activity size={16} className="text-blue-400 mb-1" />
                        <div className="text-[10px] uppercase text-text-muted font-bold tracking-wider mb-0.5">Steps</div>
                        <div className="text-lg font-bold text-white">{(log.steps || 0).toLocaleString()}</div>
                    </div>

                    {/* Intake */}
                    <div className="glass-card p-3 flex flex-col items-center justify-center text-center">
                        <Utensils size={16} className="text-rose-400 mb-1" />
                        <div className="text-[10px] uppercase text-text-muted font-bold tracking-wider mb-0.5">Intake</div>
                        <div className="text-lg font-bold text-white">
                            {Math.round(log.meals?.reduce((sum, meal) => sum + (meal.items || []).reduce((s, i) => s + (i.calories || 0) * (i.quantity || 1), 0), 0) || 0)} <span className="text-[10px] font-normal text-text-muted">kcal</span>
                        </div>
                    </div>

                    {/* Mood */}
                    <div className="glass-card p-3 flex flex-col items-center justify-center text-center">
                        <Zap size={16} className="text-yellow-400 mb-1" />
                        <div className="text-[10px] uppercase text-text-muted font-bold tracking-wider mb-0.5">Mood</div>
                        <div className="text-sm font-bold text-white truncate max-w-full px-1">{log.mood || "-"}</div>
                    </div>
                </motion.div>
            )}

            {/* Food Section */}
            <motion.div
                id="section-nutrition"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className={clsx("glass-card", activeMealIndex !== null ? "relative z-30" : "relative z-0")}
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Utensils size={14} className="text-primary" />
                        <h2 className="text-xs font-bold uppercase tracking-widest text-text-muted">Nutrition</h2>
                    </div>
                    <button
                        onClick={() => setShowComingSoon(true)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-text-muted hover:bg-white/10 transition-colors"
                    >
                        <span>Speak to Fill</span>
                        <Mic size={14} />
                    </button>
                </div>

                <AnimatePresence>
                    {showNutritionVoice && (
                        <VoiceParser
                            onCancel={() => setShowNutritionVoice(false)}
                            variant="section"
                            placeholder="I had oatmeal for breakfast and a salad for lunch."
                            onSave={(parsedData) => {
                                const newMeals = [...(log.meals || [])];
                                if (parsedData.meals) {
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    parsedData.meals.forEach((parsedMeal: any) => {
                                        const targetIndex = newMeals.findIndex(m => m.name.toLowerCase() === parsedMeal.name.toLowerCase());
                                        if (targetIndex >= 0) {
                                            newMeals[targetIndex].items = [...newMeals[targetIndex].items, ...parsedMeal.items];
                                        }
                                    });
                                }
                                const updatedLog = {
                                    ...log,
                                    meals: newMeals,
                                    water: parsedData.water ? (log.water + parsedData.water) : log.water,
                                    supplements: parsedData.supplements ? { ...log.supplements, ...parsedData.supplements } : log.supplements
                                };
                                setLog(updatedLog);
                                setShowNutritionVoice(false);
                            }}
                        />
                    )}
                </AnimatePresence>

                <div className="space-y-3">
                    {log.meals?.map((meal, index) => (
                        <motion.div
                            key={index}
                            layout
                            className={clsx(
                                "p-3 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors relative",
                                activeMealIndex === index ? "z-30" : "z-0"
                            )}
                        >
                            <div className="flex justify-between items-center mb-3">
                                <div className="text-xs font-bold text-white">{meal.name}</div>
                                <button
                                    onClick={() => setActiveMealIndex(activeMealIndex === index ? null : index)}
                                    className="text-[10px] text-primary hover:text-primary-soft transition-colors flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 hover:bg-primary/20"
                                >
                                    <Plus size={10} /> Add Item
                                </button>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {meal.items.map((item, i) => (
                                    <div key={i} className="pl-1 pr-2 py-1 rounded-lg bg-black/40 border border-white/10 text-[10px] flex items-center gap-2">
                                        <div className="flex items-center gap-1 bg-white/5 rounded-md px-1 py-0.5">
                                            <button
                                                onClick={() => updateFoodQuantity(index, i, -1)}
                                                className="hover:text-red-400 transition-colors p-0.5"
                                            >
                                                <Minus size={10} />
                                            </button>
                                            <span className="font-bold text-white min-w-[12px] text-center">{item.quantity}</span>
                                            <button
                                                onClick={() => updateFoodQuantity(index, i, 1)}
                                                className="hover:text-green-400 transition-colors p-0.5"
                                            >
                                                <Plus size={10} />
                                            </button>
                                        </div>
                                        <span className="text-gray-300">{item.name}</span>
                                    </div>
                                ))}
                                {meal.items.length === 0 && <span className="text-[10px] text-text-muted italic">No items logged</span>}
                            </div>

                            <AnimatePresence>
                                {activeMealIndex === index && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="mt-3 relative"
                                    >
                                        <div className="relative">
                                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                                            <input
                                                autoFocus
                                                type="text"
                                                placeholder="Search food..."
                                                className="input-field pl-9"
                                                value={foodQuery}
                                                onChange={(e) => setFoodQuery(e.target.value)}
                                            />
                                            {isSearching && <Loader2 size={16} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-primary" />}
                                        </div>
                                        {foodResults.length > 0 && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="absolute top-full left-0 w-full bg-[#0f172a] border border-white/10 rounded-xl mt-2 z-20 overflow-hidden shadow-xl"
                                            >
                                                {foodResults.map((food) => (
                                                    <button
                                                        key={food.id}
                                                        className="w-full p-3 text-xs border-b border-white/5 hover:bg-white/5 flex justify-between items-center text-left transition-colors"
                                                        onClick={() => addFoodToMeal(food)}
                                                    >
                                                        <span className="text-white font-medium">{food.name}</span>
                                                        <span className="text-text-muted">{food.calories} kcal</span>
                                                    </button>
                                                ))}
                                            </motion.div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Lifestyle & Recovery Section */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="glass-card relative"
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Sun size={14} className="text-green-500" />
                        <h2 className="text-xs font-bold uppercase tracking-widest text-text-muted">Lifestyle & Recovery</h2>
                    </div>
                    <button
                        onClick={() => setShowComingSoon(true)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-text-muted hover:bg-white/10 transition-colors"
                    >
                        <span>Speak to Fill</span>
                        <Mic size={14} />
                    </button>
                </div>

                <AnimatePresence>
                    {showLifestyleVoice && (
                        <VoiceParser
                            onCancel={() => setShowLifestyleVoice(false)}
                            variant="section"
                            placeholder="I drank 2 beers and smoked 5 cigarettes."
                            onSave={(parsedData) => {
                                const updatedLog = {
                                    ...log,
                                    sleep: parsedData.sleep || log.sleep,
                                    stress: parsedData.stress || log.stress,
                                    meditation: parsedData.meditation || log.meditation,
                                    sunlight: parsedData.sunlight || log.sunlight,
                                    screenTime: parsedData.screenTime || log.screenTime,
                                    alcohol: parsedData.alcohol ? {
                                        type: parsedData.alcohol.type || log.alcohol?.type || "",
                                        units: parsedData.alcohol.units ?? log.alcohol?.units ?? 0
                                    } : log.alcohol,
                                    smoking: parsedData.smoking ? {
                                        type: parsedData.smoking.type || log.smoking?.type || "No",
                                        quantity: parsedData.smoking.quantity ?? log.smoking?.quantity ?? 0
                                    } : log.smoking,
                                };
                                setLog(updatedLog);
                                setShowLifestyleVoice(false);
                            }}
                        />
                    )}
                </AnimatePresence>

                <div className="space-y-4">
                    {/* Alcohol */}
                    <div className="p-3 rounded-xl bg-white/5 border border-white/5">
                        <div className="flex items-center gap-2 mb-2 text-text-muted text-[10px] font-bold uppercase tracking-wider">
                            <Wine size={12} /> Alcohol Intake <span className="text-white/50 text-[9px] ml-auto">(units)</span>
                        </div>
                        <div className="flex gap-3">
                            <div className="relative flex-1">
                                <select
                                    value={log.alcohol?.type || ""}
                                    onChange={(e) => setLog({ ...log, alcohol: { ...log.alcohol, type: e.target.value, units: e.target.value === "None" ? 0 : (log.alcohol?.units || 0) } })}
                                    className="w-full appearance-none bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-base md:text-xs text-white outline-none focus:border-green-500/50 transition-colors"
                                >
                                    <option value="" disabled>Select Type</option>
                                    <option value="None">None</option>
                                    <option value="Beer">Beer</option>
                                    <option value="Wine">Wine</option>
                                    <option value="Whisky">Whisky</option>
                                    <option value="Vodka">Vodka</option>
                                    <option value="Gin">Gin</option>
                                    <option value="Rum">Rum</option>
                                    <option value="Cocktail">Cocktail</option>
                                </select>
                                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                            </div>
                            <div className={clsx("flex items-center bg-black/20 border border-white/10 rounded-xl transition-opacity", (log.alcohol?.type === "None" || !log.alcohol?.type) && "opacity-50 pointer-events-none")}>
                                <button
                                    disabled={log.alcohol?.type === "None" || !log.alcohol?.type}
                                    onClick={() => setLog({ ...log, alcohol: { ...log.alcohol!, units: Math.max(0, (log.alcohol?.units || 0) - 1) } })}
                                    className="px-3 py-2 text-text-muted hover:text-white transition-colors disabled:cursor-not-allowed"
                                >
                                    <Minus size={12} />
                                </button>
                                <span className="w-8 text-center text-sm font-bold text-white">{log.alcohol?.units || 0}</span>
                                <button
                                    disabled={log.alcohol?.type === "None" || !log.alcohol?.type}
                                    onClick={() => setLog({ ...log, alcohol: { ...log.alcohol!, units: (log.alcohol?.units || 0) + 1 } })}
                                    className="px-3 py-2 text-text-muted hover:text-white transition-colors disabled:cursor-not-allowed"
                                >
                                    <Plus size={12} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Smoking */}

                    <div className="p-3 rounded-xl bg-white/5 border border-white/5">
                        <div className="flex items-center gap-2 mb-2 text-text-muted text-[10px] font-bold uppercase tracking-wider">
                            <Cigarette size={12} /> Smoking/Vaping <span className="text-white/50 text-[9px] ml-auto">(Daily)</span>
                        </div>
                        <div className="flex gap-3">
                            <div className="relative flex-1">
                                <select
                                    value={log.smoking?.type === "Yes" ? "Yes" : "No"}
                                    onChange={(e) => setLog({ ...log, smoking: { type: e.target.value, quantity: e.target.value === "No" ? 0 : (log.smoking?.quantity || 0) } })}
                                    className="w-full appearance-none bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-base md:text-xs text-white outline-none focus:border-green-500/50 transition-colors"
                                >
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                </select>
                                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                            </div>

                            {/* Conditional Quantity Counter */}
                            {log.smoking?.type === "Yes" && (
                                <div className="flex items-center bg-black/20 border border-white/10 rounded-xl">
                                    <button
                                        onClick={() => setLog({ ...log, smoking: { ...log.smoking!, quantity: Math.max(0, (log.smoking?.quantity || 0) - 1) } })}
                                        className="px-3 py-2 text-text-muted hover:text-white transition-colors"
                                    >
                                        <Minus size={12} />
                                    </button>
                                    <span className="w-8 text-center text-sm font-bold text-white">{log.smoking.quantity}</span>
                                    <button
                                        onClick={() => setLog({ ...log, smoking: { ...log.smoking!, quantity: (log.smoking?.quantity || 0) + 1 } })}
                                        className="px-3 py-2 text-text-muted hover:text-white transition-colors"
                                    >
                                        <Plus size={12} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stress */}
                    <div>
                        <div className="flex items-center gap-2 mb-2 text-text-muted text-[10px] font-bold uppercase tracking-wider">
                            <Activity size={12} /> Stress Today (3/5)
                        </div>
                        <div className="grid grid-cols-5 gap-2">
                            {[1, 2, 3, 4, 5].map((level) => {
                                const colors = {
                                    1: "bg-green-500/20 border-green-500/50 text-green-500",
                                    2: "bg-lime-500/20 border-lime-500/50 text-lime-500",
                                    3: "bg-yellow-500/20 border-yellow-500/50 text-yellow-500",
                                    4: "bg-orange-500/20 border-orange-500/50 text-orange-500",
                                    5: "bg-red-500/20 border-red-500/50 text-red-500",
                                };
                                return (
                                    <button
                                        key={level}
                                        onClick={() => setLog({ ...log, stress: level })}
                                        className={clsx(
                                            "py-2 rounded-xl text-xs font-bold border transition-all",
                                            log.stress === level
                                                ? (colors[level as keyof typeof colors] || "bg-amber-500/20 border-amber-500/50 text-amber-500")
                                                : "bg-white/5 border-white/5 text-text-muted hover:bg-white/10"
                                        )}
                                    >
                                        {level}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex justify-between mt-1 px-1">
                            <span className="text-[10px] text-text-muted">Low</span>
                            <span className="text-[10px] text-text-muted">High</span>
                        </div>
                    </div>

                    {/* Meditation */}
                    <div>
                        <div className="flex items-center gap-2 mb-2 text-text-muted text-[10px] font-bold uppercase tracking-wider">
                            <Flower2 size={12} /> Meditation (min)
                        </div>
                        <input
                            type="number"
                            className="input-field [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            value={log.meditation || ""}
                            placeholder="e.g. 15"
                            onChange={(e) => setLog({ ...log, meditation: parseInt(e.target.value) || 0 })}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        {/* Sunlight */}
                        <div>
                            <div className="flex items-center gap-2 mb-2 text-text-muted text-[10px] font-bold uppercase tracking-wider">
                                <Sun size={12} /> Sunlight (min)
                            </div>
                            <input
                                type="number"
                                className="input-field [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                value={log.sunlight || ""}
                                placeholder="e.g. 15"
                                onChange={(e) => setLog({ ...log, sunlight: parseInt(e.target.value) || 0 })}
                            />
                        </div>

                        {/* Screen Time */}
                        <div>
                            <div className="flex items-center gap-2 mb-2 text-text-muted text-[10px] font-bold uppercase tracking-wider">
                                <Smartphone size={12} /> Screen Time (h)
                            </div>
                            <input
                                type="number"
                                className="input-field [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                value={log.screenTime || ""}
                                placeholder="e.g. 3"
                                onChange={(e) => setLog({ ...log, screenTime: parseInt(e.target.value) || 0 })}
                            />
                        </div>
                    </div>

                    {/* Screen Free Checkbox */}
                    <div className="flex items-center justify-end gap-3 py-1">
                        <button
                            onClick={() => setLog({ ...log, screenFreeBeforeBed: !log.screenFreeBeforeBed })}
                            className={clsx(
                                "w-5 h-5 rounded border flex items-center justify-center transition-colors",
                                log.screenFreeBeforeBed ? "bg-green-500 border-green-500" : "border-white/20 bg-white/5"
                            )}
                        >
                            {log.screenFreeBeforeBed && <Check size={14} className="text-black" />}
                        </button>
                        <span className="text-xs text-gray-300">Screen-free before bed (+1)</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        {/* Social Connection */}
                        <div>
                            <div className="flex items-center gap-2 mb-2 text-text-muted text-[10px] font-bold uppercase tracking-wider">
                                <Zap size={12} /> Social Connection (3/5)
                            </div>
                            <div className="grid grid-cols-5 gap-1">
                                {[1, 2, 3, 4, 5].map((level) => (
                                    <button
                                        key={level}
                                        onClick={() => setLog({ ...log, socialConnect: level })}
                                        className={clsx(
                                            "py-2 rounded-xl text-xs font-bold border transition-all",
                                            log.socialConnect === level
                                                ? "bg-blue-500/20 border-blue-500/50 text-blue-400"
                                                : "bg-white/5 border-white/5 text-text-muted hover:bg-white/10"
                                        )}
                                    >
                                        {level}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Mood Duplicate (Synced) */}
                        <div>
                            <div className="flex items-center gap-2 mb-2 text-text-muted text-[10px] font-bold uppercase tracking-wider">
                                <Moon size={12} /> Mood
                            </div>
                            <div className="relative">
                                <select
                                    value={log.mood || ""}
                                    onChange={(e) => setLog({ ...log, mood: e.target.value })}
                                    className="w-full appearance-none bg-black/20 border border-white/10 rounded-xl px-3 py-2.5 text-base md:text-xs text-white outline-none focus:border-green-500/50 transition-colors"
                                >
                                    <option value="" disabled>Select Mood</option>
                                    <option value="Energetic">Energetic</option>
                                    <option value="Calm">Calm</option>
                                    <option value="Focused">Focused</option>
                                    <option value="Happy">Happy</option>
                                    <option value="Tired">Tired</option>
                                    <option value="Stressed">Stressed</option>
                                    <option value="Anxious">Anxious</option>
                                    <option value="Irritable">Irritable</option>
                                </select>
                                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Workout Section */}
            <motion.div
                id="section-workout"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={clsx("glass-card relative", isWorkoutDropdownOpen !== null ? "z-[60]" : "z-0")}
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Dumbbell size={14} className="text-primary" />
                        <h2 className="text-xs font-bold uppercase tracking-widest text-text-muted">Workout & Activity</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                const newWorkouts = [...(log.workouts || []), { type: "Rest", duration: 0, calories: 0 }];
                                setLog({ ...log, workouts: newWorkouts });
                            }}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-[10px] font-bold text-primary hover:bg-primary/20 transition-colors"
                        >
                            <Plus size={12} />
                            <span>Add Session</span>
                        </button>
                        <button
                            onClick={() => setShowComingSoon(true)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-text-muted hover:bg-white/10 transition-colors"
                        >
                            <Mic size={14} />
                        </button>
                    </div>
                </div>

                <AnimatePresence>
                    {showWorkoutVoice && (
                        <VoiceParser
                            onCancel={() => setShowWorkoutVoice(false)}
                            variant="section"
                            placeholder="I did a 45 minute chest workout and walked 5000 steps."
                            onSave={(parsedData) => {
                                const updatedLog = {
                                    ...log,
                                    workouts: parsedData.workouts || log.workouts,
                                    steps: parsedData.steps || log.steps
                                };
                                setLog(updatedLog);
                                setShowWorkoutVoice(false);
                            }}
                        />
                    )}
                </AnimatePresence>

                <div className="mb-4 pb-4 border-b border-white/5">
                    <label className="block text-[10px] font-semibold text-text-muted mb-1.5 uppercase tracking-wider flex items-center gap-1">
                        <Activity size={10} /> Daily Steps
                    </label>
                    <input
                        type="number"
                        className="input-field [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-primary/5 border-primary/10"
                        value={log.steps || ""}
                        placeholder="e.g. 10000"
                        onChange={(e) => setLog({ ...log, steps: parseInt(e.target.value) || 0 })}
                    />
                </div>

                <div className="space-y-6">
                    {(log.workouts || []).map((workoutSession, idx) => (
                        <div key={idx} className="relative p-3 rounded-xl bg-white/5 border border-white/5">
                            {log.workouts.length > 1 && (
                                <button
                                    onClick={() => {
                                        const newWorkouts = log.workouts.filter((_, i) => i !== idx);
                                        setLog({ ...log, workouts: newWorkouts });
                                    }}
                                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all z-10"
                                >
                                    <Minus size={12} />
                                </button>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <label className="block text-[10px] font-semibold text-text-muted mb-1.5 uppercase tracking-wider">Workout {idx + 1} Type</label>
                                    <div className="relative">
                                        <button
                                            onClick={() => {
                                                setActiveMealIndex(null); // Close nutrition if open
                                                setIsWorkoutDropdownOpen(isWorkoutDropdownOpen === idx ? -1 as any : idx);
                                            }}
                                            className="input-field w-full text-left flex items-center justify-between"
                                        >
                                            <span className={clsx(!workoutSession.type && "text-text-muted")}>
                                                {workoutSession.type || "Select Workout"}
                                            </span>
                                            <ChevronDown size={14} className="text-text-muted" />
                                        </button>

                                        <AnimatePresence>
                                            {isWorkoutDropdownOpen === idx && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 5 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 5 }}
                                                    className="absolute top-full left-0 w-full bg-[#0f172a] border border-white/10 rounded-xl mt-2 z-[70] overflow-hidden shadow-xl max-h-60 flex flex-col"
                                                >
                                                    <div className="p-2 border-b border-white/5 sticky top-0 bg-[#0f172a] z-10">
                                                        <div className="relative">
                                                            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                placeholder="Search workout..."
                                                                className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-base md:text-xs text-white outline-none focus:border-primary/50 transition-colors"
                                                                value={workoutSearchQuery}
                                                                onChange={(e) => setWorkoutSearchQuery(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="overflow-y-auto flex-1 p-1">
                                                        {Object.entries(WORKOUT_OPTIONS).map(([category, options]) => {
                                                            const filteredOptions = options.filter(opt =>
                                                                opt.toLowerCase().includes(workoutSearchQuery.toLowerCase())
                                                            );

                                                            if (filteredOptions.length === 0) return null;

                                                            return (
                                                                <div key={category} className="mb-2 last:mb-0">
                                                                    <div className="px-2 py-1 text-[10px] font-bold text-text-muted uppercase tracking-wider sticky top-0 bg-[#0f172a]/95 backdrop-blur-sm">
                                                                        {category}
                                                                    </div>
                                                                    {filteredOptions.map(option => (
                                                                        <button
                                                                            key={option}
                                                                            onClick={() => {
                                                                                const newWorkouts = [...log.workouts];
                                                                                newWorkouts[idx] = { ...newWorkouts[idx], type: option };
                                                                                setLog({ ...log, workouts: newWorkouts });
                                                                                setIsWorkoutDropdownOpen(null);
                                                                                setWorkoutSearchQuery("");
                                                                            }}
                                                                            className={clsx(
                                                                                "w-full text-left px-2 py-1.5 text-xs rounded-md transition-colors flex items-center justify-between group",
                                                                                workoutSession.type === option ? "bg-primary/20 text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"
                                                                            )}
                                                                        >
                                                                            {option}
                                                                            {workoutSession.type === option && <Check size={12} className="text-primary" />}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-[10px] font-semibold text-text-muted mb-1.5 uppercase tracking-wider">Duration (min)</label>
                                    <input
                                        type="number"
                                        className="input-field [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        value={workoutSession.duration || ""}
                                        placeholder="e.g. 45"
                                        onChange={(e) => {
                                            const newWorkouts = [...log.workouts];
                                            newWorkouts[idx] = { ...newWorkouts[idx], duration: parseInt(e.target.value) || 0 };
                                            setLog({ ...log, workouts: newWorkouts });
                                        }}
                                    />
                                </div>
                                <div className="col-span-1 flex items-end">
                                    <div className="w-full">
                                        <label className="block text-[10px] font-semibold text-text-muted mb-1.5 uppercase tracking-wider">Calories (est)</label>
                                        <div className="text-[10px] text-white/50 px-1 italic">
                                            Auto-calculated
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div >

            {/* Backdrop for workout dropdown */}
            {isWorkoutDropdownOpen !== null && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsWorkoutDropdownOpen(null)}
                />
            )}

            {/* Sleep & Supplements */}
            <motion.div
                id="section-sleep"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-card relative"
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Moon size={14} className="text-primary" />
                        <h2 className="text-xs font-bold uppercase tracking-widest text-text-muted">Sleep, Water & Supplements</h2>
                    </div>
                    <button
                        onClick={() => setShowComingSoon(true)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-text-muted hover:bg-white/10 transition-colors"
                    >
                        <span>Speak to Fill</span>
                        <Mic size={14} />
                    </button>
                </div>

                <AnimatePresence>
                    {showSleepVoice && (
                        <VoiceParser
                            onCancel={() => setShowSleepVoice(false)}
                            variant="section"
                            placeholder="I slept 7 hours, drank 2 liters of water, and took magnesium."
                            onSave={(parsedData) => {
                                const updatedLog = {
                                    ...log,
                                    sleep: parsedData.sleep || log.sleep,
                                    water: parsedData.water || log.water,
                                    supplements: parsedData.supplements ? { ...log.supplements, ...parsedData.supplements } : log.supplements
                                };
                                setLog(updatedLog);
                                setShowSleepVoice(false);
                            }}
                        />
                    )}
                </AnimatePresence>

                <div className="grid grid-cols-2 gap-3 mb-6">
                    <div>
                        <label className="block text-[10px] font-semibold text-text-muted mb-1.5 uppercase tracking-wider">Sleep (hrs)</label>
                        <input
                            type="number"
                            className="input-field"
                            value={(typeof log.sleep === 'object' ? log.sleep.hours : log.sleep) || ""}
                            placeholder="e.g. 5"
                            onChange={(e) => setLog({ ...log, sleep: { hours: parseFloat(e.target.value) || 0, quality: (typeof log.sleep === 'object' ? log.sleep.quality : undefined) } })}
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-semibold text-text-muted mb-1.5 uppercase tracking-wider flex items-center gap-1">
                            <Droplets size={10} /> Water (L)
                        </label>
                        <input
                            type="number"
                            className="input-field"
                            value={log.water || ""}
                            placeholder="e.g. 2.5"
                            onChange={(e) => setLog({ ...log, water: parseFloat(e.target.value) || 0 })}
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="block text-[10px] font-semibold text-text-muted mb-2 uppercase tracking-wider flex items-center gap-1">
                        <Pill size={10} /> Supplements
                    </label>

                    {/* Whey Protein Scoops - Moved here */}
                    <div className="mb-3 pb-3 border-b border-white/5">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] font-semibold text-text-muted uppercase tracking-wider flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span> Whey Protein (Scoops)
                            </label>
                            <div className="flex items-center gap-3 bg-white/5 rounded-lg p-1 border border-white/5">
                                <button
                                    onClick={() => setLog(prev => ({ ...prev, wheyScoops: Math.max(0, (prev.wheyScoops || 0) - 1) }))}
                                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-white/10 text-text-muted hover:text-white transition-colors"
                                >
                                    <Minus size={14} />
                                </button>
                                <span className="text-sm font-bold w-4 text-center">{log.wheyScoops || 0}</span>
                                <button
                                    onClick={() => setLog(prev => ({ ...prev, wheyScoops: (prev.wheyScoops || 0) + 1 }))}
                                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-white/10 text-white transition-colors bg-white/5"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        </div>
                    </div>


                    <div className="grid grid-cols-2 gap-2">
                        {Object.entries(log.supplements || {}).map(([name, checked]) => (
                            <button
                                key={name}
                                onClick={() => toggleSupplement(name)}
                                className={clsx(
                                    "flex items-center gap-2 p-2 rounded-lg border text-[10px] font-medium transition-all duration-200",
                                    checked
                                        ? "bg-primary/10 border-primary/30 text-white"
                                        : "bg-white/5 border-white/5 text-text-muted hover:bg-white/10"
                                )}
                            >
                                <div className={clsx(
                                    "w-4 h-4 rounded-full border flex items-center justify-center transition-colors",
                                    checked ? "bg-primary border-primary" : "border-gray-500"
                                )}>
                                    {checked && <Check size={10} className="text-black" />}
                                </div>
                                {name}
                            </button>
                        ))}

                        {/* Add Custom Supplement */}
                        <div className="col-span-2 mt-2 relative">
                            <div className="relative w-full">
                                <input
                                    type="text"
                                    className="input-field py-2 text-xs w-full"
                                    placeholder="Add Supplement"
                                    value={newSupplement}
                                    onChange={(e) => {
                                        setNewSupplement(e.target.value);
                                        setShowSuppSuggestions(true);
                                    }}
                                    onFocus={() => setShowSuppSuggestions(true)}
                                    onBlur={() => setTimeout(() => setShowSuppSuggestions(false), 200)} // Delay to allow click
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addCustomSupplement();
                                            setShowSuppSuggestions(false);
                                        }
                                    }}
                                />
                                <AnimatePresence>
                                    {showSuppSuggestions && newSupplement.length > 0 && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute bottom-full left-0 w-full mb-1 bg-[#0f172a] border border-white/10 rounded-xl overflow-hidden shadow-xl z-50 max-h-40 overflow-y-auto"
                                        >
                                            {COMMON_SUPPLEMENTS
                                                .filter(s => s.toLowerCase().includes(newSupplement.toLowerCase()) && !log.supplements?.[s])
                                                .slice(0, 5)
                                                .map((supp) => (
                                                    <button
                                                        key={supp}
                                                        className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-white/10 hover:text-white transition-colors block"
                                                        onClick={() => {
                                                            setLog({
                                                                ...log,
                                                                supplements: {
                                                                    ...log.supplements,
                                                                    [supp]: true
                                                                }
                                                            });
                                                            setNewSupplement("");
                                                            setShowSuppSuggestions(false);
                                                        }}
                                                    >
                                                        {supp}
                                                    </button>
                                                ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>


                </div>
                <div className="mt-6 pt-4 border-t border-white/5">
                    {/* Summary Chips (Duplicate for footer) */}
                    <div className="grid grid-cols-2 gap-2 mb-6">
                        {/* Food Summary */}
                        <div
                            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex items-center gap-2 w-full"
                        >
                            <div className={clsx("w-2 h-2 rounded-full", log.meals?.some(m => m.items.length > 0) ? "bg-green-500" : "bg-gray-600")}></div>
                            <span className="text-text-muted text-xs truncate">Food • <span className="text-white">{log.meals?.filter(m => m.items.length > 0).length || 0}/4</span></span>
                        </div>

                        {/* Workout Summary */}
                        <div
                            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex items-center gap-2 w-full"
                        >
                            <div className={clsx("w-2 h-2 rounded-full", log.workouts?.some(w => w.type !== "Rest") ? "bg-green-500" : "bg-gray-600")}></div>
                            <span className="text-text-muted text-xs truncate">Workout • <span className="text-white">{log.workouts?.some(w => w.type !== "Rest") ? `${log.workouts.filter(w => w.type !== "Rest").length} sessions` : "Rest"}</span></span>
                        </div>

                        {/* Sleep Summary */}
                        <div
                            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex items-center gap-2 w-full"
                        >
                            <div className={clsx("w-2 h-2 rounded-full", getSleepHours(log.sleep) >= 7 ? "bg-green-500" : "bg-amber-500")}></div>
                            <span className="text-text-muted text-xs truncate">Sleep • <span className="text-white">{getSleepHours(log.sleep)} h</span></span>
                        </div>

                        {/* Water Summary */}
                        <div
                            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex items-center gap-2 w-full"
                        >
                            <div className={clsx("w-2 h-2 rounded-full", log.water >= 2 ? "bg-green-500" : "bg-blue-400")}></div>
                            <span className="text-text-muted text-xs truncate">Water • <span className="text-white">{log.water} L</span></span>
                        </div>
                    </div>

                    <button
                        onClick={() => {
                            if (pastLogs.some(l => l.date === log.date)) {
                                setShowSaveConfirm(true);
                            } else {
                                onSave(log);
                            }
                        }}
                        className="w-full primary-btn py-3 text-sm font-bold tracking-wider shadow-lg shadow-primary/20"
                    >
                        {pastLogs.some(l => l.date === log.date) ? "Update Daily Log" : "Save Daily Log"}
                    </button>
                </div>

            </motion.div >

            {/* Save Confirmation Modal */}
            <SimpleModal
                isOpen={showSaveConfirm}
                onClose={() => setShowSaveConfirm(false)}
                title={pastLogs.some(l => l.date === log.date) ? "Confirm Update" : "Confirm Save"}
            >
                <div className="flex flex-col items-center justify-center space-y-4 text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check size={32} className="text-primary" />
                    </div>
                    <p className="text-sm text-gray-300">
                        {pastLogs.some(l => l.date === log.date)
                            ? "Are you sure you want to update the log?"
                            : "Please confirm that you wish to save these records."}
                    </p>
                    <div className="flex gap-3 w-full mt-2">
                        <button
                            onClick={() => setShowSaveConfirm(false)}
                            className="flex-1 px-4 py-2 rounded-xl border border-white/10 text-white text-sm font-medium hover:bg-white/5 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                onSave(log);
                                setShowSaveConfirm(false);
                            }}
                            className="flex-1 primary-btn py-2 text-sm font-bold"
                        >
                            Confirm Save
                        </button>
                    </div>
                </div>
            </SimpleModal>

            {/* Coming Soon Modal */}
            <SimpleModal
                isOpen={showComingSoon}
                onClose={() => setShowComingSoon(false)}
                title="Coming Soon"
            >
                <div className="flex flex-col items-center justify-center space-y-4 text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Rocket size={32} className="text-primary" />
                    </div>
                    <p className="text-sm text-gray-300">
                        We’re improving our AI voice feature to work better than ever. Coming in the next update!
                    </p>
                    <button
                        onClick={() => setShowComingSoon(false)}
                        className="primary-btn mt-2"
                    >
                        Got it
                    </button>
                </div>
            </SimpleModal>
        </div>
    );
}
